// ====================================================================================================
//  File Name   : NeuralPulsePage.js
//  Location    : frontend/src/features/dashboard/NeuralPulsePage.js
//  Project     : AIRS Security - Autonomous Sentinel Ecosystem
//  Developed By: Appence Software Solution | https://appence.com
//  Legal Notice: Proprietary Property. Unauthorized use strictly prohibited. (c) 2026
// ====================================================================================================

import React, { useEffect, useRef, useState, useCallback } from "react";
import axiosClient from "../../api/axiosClient";

// ─── SVG Icon components ──────────────────────────────────────────────────────
const CpuIcon = () => (
  <svg className="w-4 h-4 mr-2 text-cyan-400 filter drop-shadow-[0_0_4px_rgba(34,211,238,0.4)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 5h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2z" />
  </svg>
);
const DatabaseIcon = () => (
  <svg className="w-4 h-4 mr-2 text-purple-400 filter drop-shadow-[0_0_4px_rgba(192,132,252,0.4)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
  </svg>
);
const ActivityIcon = () => (
  <svg className="w-4 h-4 mr-2 text-emerald-400 filter drop-shadow-[0_0_4px_rgba(52,211,153,0.4)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);
const TerminalIcon = () => (
  <svg className="w-4 h-4 mr-2 text-amber-500 filter drop-shadow-[0_0_4px_rgba(245,158,11,0.4)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z" />
  </svg>
);
const ShieldIcon = () => (
  <svg className="w-4 h-4 mr-2 text-red-400 filter drop-shadow-[0_0_4px_rgba(239,68,68,0.4)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// ─── Cognitive state palettes ─────────────────────────────────────────────────
const STATE_PALETTE = {
  IDLE:              { pill: "bg-slate-900/80 text-cyan-400 border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.15)]",   accent: "#06b6d4", dim: "rgba(6,182,212,0.08)"   },
  THINKING:          { pill: "bg-emerald-950/80 text-emerald-400 border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.15)]", accent: "#10b981", dim: "rgba(16,185,129,0.08)"   },
  DREAMING:          { pill: "bg-purple-950/80 text-purple-400 border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.15)]",    accent: "#a855f7", dim: "rgba(168,85,247,0.08)"  },
  TACTICAL_REACTION: { pill: "bg-red-950/80 text-red-400 border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.15)]",              accent: "#ef4444", dim: "rgba(239,68,68,0.10)"   },
};

// ─── Brain regions — Meticulously Adjusted Spatial Mapping (No Overlaps) ───
const BRAIN_REGIONS = [
  // LEFT HEMISPHERE
  { id:"L_prefrontal", label:"Prefrontal Cortex",    cx:160, cy: 110, rx:30, ry:30, side:"L", color:"#03152d", activeColor:"#06b6d4", glowColor:"rgba(6,182,212,0.8)",  pipeline:"embedding", desc:"Token embedding & input projection" },
  { id:"L_motor",      label:"Motor Cortex",        cx:150, cy: 195, rx:28, ry:28, side:"L", color:"#02170c", activeColor:"#10b981", glowColor:"rgba(16,185,129,0.8)",  pipeline:"attention", desc:"Attention head alignment routing" },
  { id:"L_parietal",   label:"Parietal Lobe",       cx:230, cy: 145, rx:28, ry:28, side:"L", color:"#100926", activeColor:"#a855f7", glowColor:"rgba(168,85,247,0.8)", pipeline:"mlp",       desc:"MLP hidden layer computation" },
  { id:"L_temporal",   label:"Temporal Lobe",       cx:150, cy: 280, rx:28, ry:28, side:"L", color:"#100926", activeColor:"#c084fc", glowColor:"rgba(192,132,252,0.8)", pipeline:"mlp",       desc:"Contextual memory retrieval" },
  { id:"L_occipital",  label:"Occipital Lobe",      cx:210, cy: 350, rx:28, ry:28, side:"L", color:"#1a0f02", activeColor:"#f59e0b", glowColor:"rgba(245,158,11,0.8)",  pipeline:"output",    desc:"Output logit projection" },
  { id:"cerebellum_L", label:"Cerebellum (L)",      cx:245, cy: 420, rx:26, ry:26, side:"L", color:"#0a061a", activeColor:"#8b5cf6", glowColor:"rgba(139,92,246,0.8)",  pipeline:"dreaming",  desc:"Project Ouroboros dream loop" },
  
  // CENTER AXIS
  { id:"corpus",       label:"Corpus Callosum",     cx:300, cy: 205, rx:26, ry:36, side:"C", color:"#1f1602", activeColor:"#fbbf24", glowColor:"rgba(251,191,36,0.8)",  pipeline:"gate",      desc:"Constitutional Assurance Gate" },
  { id:"brainstem",    label:"Brainstem",           cx:300, cy: 375, rx:26, ry:34, side:"C", color:"#240505", activeColor:"#ef4444", glowColor:"rgba(239,68,68,0.8)",   pipeline:"gate",      desc:"Tactical reaction override bus" },
  
  // RIGHT HEMISPHERE
  { id:"R_prefrontal", label:"Prefrontal (R)",      cx:440, cy: 110, rx:30, ry:30, side:"R", color:"#03152d", activeColor:"#06b6d4", glowColor:"rgba(6,182,212,0.7)",  pipeline:"embedding", desc:"Memory association index lookup" },
  { id:"R_motor",      label:"Motor Cortex (R)",    cx:450, cy: 195, rx:28, ry:28, side:"R", color:"#02170c", activeColor:"#10b981", glowColor:"rgba(16,185,129,0.7)",  pipeline:"attention", desc:"Cross-attention right routing" },
  { id:"R_parietal",   label:"Parietal (R)",        cx:370, cy: 145, rx:28, ry:28, side:"R", color:"#100926", activeColor:"#a855f7", glowColor:"rgba(168,85,247,0.7)", pipeline:"mlp",       desc:"Episodic memory cluster" },
  { id:"R_temporal",   label:"Temporal (R)",        cx:450, cy: 280, rx:28, ry:28, side:"R", color:"#100926", activeColor:"#c084fc", glowColor:"rgba(192,132,252,0.7)", pipeline:"mlp",       desc:"Semantic relational mapping" },
  { id:"R_occipital",  label:"Occipital (R)",       cx:390, cy: 350, rx:28, ry:28, side:"R", color:"#1a0f02", activeColor:"#f59e0b", glowColor:"rgba(245,158,11,0.7)",  pipeline:"output",    desc:"Decision boundary computation" },
  { id:"R_cerebellum", label:"Cerebellum (R)",      cx:355, cy: 420, rx:26, ry:26, side:"R", color:"#0a061a", activeColor:"#8b5cf6", glowColor:"rgba(139,92,246,0.7)",  pipeline:"dreaming",  desc:"Pattern replay & consolidation" },
];

const PIPELINE_REGION_MAP = {
  embedding:  ["L_prefrontal","R_prefrontal"],
  attention:  ["L_motor","R_motor","corpus"],
  mlp:        ["L_parietal","L_temporal","R_parietal","R_temporal"],
  output:     ["L_occipital","R_occipital"],
  gate:       ["corpus","brainstem"],
  dreaming:   ["cerebellum_L","R_cerebellum"],
  reaction:   ["brainstem","corpus","L_prefrontal"],
};

const SIGNAL_PATHS = [
  { from:"L_prefrontal", to:"L_parietal",   stage:0 },
  { from:"L_parietal",   to:"corpus",      stage:0 },
  { from:"corpus",       to:"L_motor",     stage:1 },
  { from:"L_motor",      to:"L_temporal",  stage:1 },
  { from:"L_temporal",   to:"L_occipital", stage:2 },
  { from:"L_occipital",  to:"brainstem",   stage:3 },
  { from:"R_prefrontal", to:"R_parietal",   stage:0 },
  { from:"R_parietal",   to:"corpus",      stage:0 },
  { from:"corpus",       to:"R_motor",     stage:1 },
  { from:"R_motor",      to:"R_temporal",  stage:1 },
  { from:"R_temporal",   to:"R_occipital", stage:2 },
  { from:"R_occipital",  to:"brainstem",   stage:3 },
];

const TabBtn = ({ id, activeTab, setActiveTab, label }) => (
  <button
    onClick={() => setActiveTab(id)}
    className={`flex-1 py-3 text-[11px] font-mono font-bold tracking-widest uppercase transition-all duration-300 border-b-2 relative ${
      activeTab === id
        ? "border-cyan-500 text-cyan-400 bg-gradient-to-t from-cyan-950/30 to-transparent shadow-[inset_0_-10px_10px_-10px_rgba(6,182,212,0.2)]"
        : "border-slate-800 text-slate-500 hover:text-slate-300 hover:bg-slate-900/40"
    }`}
  >
    {activeTab === id && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-[2px] bg-cyan-400 shadow-[0_0_8px_#06b6d4]" />}
    {label}
  </button>
);

const MetricBar = ({ label, value, max, color = "bg-cyan-500", textColor = "text-cyan-400" }) => (
  <div className="group">
    <div className="flex justify-between text-[11px] mb-1.5 font-mono">
      <span className="text-slate-400 group-hover:text-slate-200 transition-colors">{label}</span>
      <span className={`${textColor} font-semibold filter drop-shadow-[0_0_3px_currentColor]`}>{(value || 0).toFixed(2)}</span>
    </div>
    <div className="h-[4px] w-full bg-slate-950 rounded-full p-[1px] border border-slate-900">
      <div
        className={`h-full ${color} rounded-full transition-all duration-1000 shadow-[0_0_6px_currentColor]`}
        style={{ width: `${Math.min(100, ((value || 0) / max) * 100)}%` }}
      />
    </div>
  </div>
);

const EegCanvas = ({ eegHistory, state }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const containerW = canvas.parentElement.clientWidth;
    canvas.width  = containerW * 2; 
    canvas.height = 140;
    ctx.scale(2, 2);

    const W    = containerW;
    const H    = 70;
    const mid  = H / 2;
    const step = W / (eegHistory.length - 1);
    const colorMap = { IDLE:"#06b6d4", THINKING:"#10b981", DREAMING:"#a855f7", TACTICAL_REACTION:"#ef4444" };
    const color = colorMap[state] || "#06b6d4";

    ctx.clearRect(0, 0, W, H);

    ctx.strokeStyle = "rgba(6,182,212,0.02)";
    ctx.lineWidth = 0.5;
    for (let y = 0; y < H; y += 12) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }
    for (let x = 0; x < W; x += 30) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }

    ctx.strokeStyle = "rgba(6,182,212,0.06)";
    ctx.beginPath(); ctx.moveTo(0, mid); ctx.lineTo(W, mid); ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, mid);
    eegHistory.forEach((v, i) => ctx.lineTo(i * step, mid + v * 0.7));
    ctx.lineTo(W, mid);
    ctx.closePath();
    ctx.fillStyle = color + "09";
    ctx.fill();

    ctx.beginPath();
    eegHistory.forEach((v, i) => i === 0 ? ctx.moveTo(0, mid + v * 0.7) : ctx.lineTo(i * step, mid + v * 0.7));
    ctx.strokeStyle = color;
    ctx.lineWidth   = 1.5;
    ctx.stroke();

    const lastV = eegHistory[eegHistory.length - 1] * 0.7;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(W - 4, mid + lastV, 3, 0, Math.PI * 2);
    ctx.fill();
  }, [eegHistory, state]);

  return <canvas ref={ref} className="w-full h-[70px] block" />;
};

const BrainSVG = ({ activeRegionSet, pulseData, activeStage, state }) => {
  const [hovered, setHovered] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60);
    return () => clearInterval(id);
  }, []);

  const stageColors = ["#06b6d4","#10b981","#a855f7","#f59e0b"];

  return (
    <svg
      viewBox="0 0 600 480"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full select-none"
    >
      <defs>
        {BRAIN_REGIONS.map(r => (
          <radialGradient key={`grad-${r.id}`} id={`grad-${r.id}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={activeRegionSet.has(r.id) ? r.activeColor : r.color} stopOpacity={activeRegionSet.has(r.id) ? 0.45 : 0.15} />
            <stop offset="100%" stopColor={activeRegionSet.has(r.id) ? r.activeColor : "#01050a"} stopOpacity={0.0} />
          </radialGradient>
        ))}
        <filter id="glow-heavy" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="glow-light" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      <g opacity="0.3">
        {Array.from({length:20}).map((_,i) => (
          <line key={`gx${i}`} x1={i*32} y1="0" x2={i*32} y2="480" stroke="#06b6d4" strokeWidth="0.5" opacity="0.04"/>
        ))}
        {Array.from({length:15}).map((_,i) => (
          <line key={`gy${i}`} x1="0" y1={i*32} x2="600" y2={i*32} stroke="#06b6d4" strokeWidth="0.5" opacity="0.04"/>
        ))}
      </g>

      <g fill="none" strokeWidth="1" opacity="0.15">
        <path d="M 300 45 C 190 45, 75 90, 65 200 C 55 285, 95 340, 130 375 C 155 400, 185 435, 235 435 C 265 435, 275 415, 300 415 C 325 415, 335 435, 365 435 C 415 435, 445 400, 470 375 C 505 340, 545 285, 535 200 C 525 90, 410 45, 300 45 Z" stroke="#06b6d4" strokeWidth="1.5" />
        <line x1="300" y1="45" x2="300" y2="415" stroke="#06b6d4" strokeWidth="1" strokeDasharray="5,5" />
      </g>

      {SIGNAL_PATHS.map((path, idx) => {
        const fr = BRAIN_REGIONS.find(r => r.id === path.from);
        const tr = BRAIN_REGIONS.find(r => r.id === path.to);
        if (!fr || !tr) return null;
        
        const mx = (fr.cx + tr.cx) / 2 + (fr.cy - tr.cy) * 0.15;
        const my = (fr.cy + tr.cy) / 2 + (tr.cx - fr.cx) * 0.05;
        const isActive = path.stage === activeStage;
        
        return (
          <path
            key={idx}
            d={`M${fr.cx},${fr.cy} Q${mx},${my} ${tr.cx},${tr.cy}`}
            fill="none"
            stroke={isActive ? stageColors[path.stage] : "rgba(6,182,212,0.08)"}
            strokeWidth={isActive ? "2" : "0.8"}
            opacity={isActive ? 0.85 : 0.4}
            strokeDasharray={isActive ? "none" : "3,5"}
          />
        );
      })}

      {SIGNAL_PATHS.filter(p => p.stage === activeStage).map((path, idx) => {
        const fr = BRAIN_REGIONS.find(r => r.id === path.from);
        const tr = BRAIN_REGIONS.find(r => r.id === path.to);
        if (!fr || !tr) return null;
        
        const mx = (fr.cx + tr.cx) / 2 + (fr.cy - tr.cy) * 0.15;
        const my = (fr.cy + tr.cy) / 2 + (tr.cx - fr.cx) * 0.05;
        const t = ((tick * 0.02 + idx * 0.35) % 1.1);
        const tc = Math.max(0, Math.min(1, t));
        
        const bx = (1-tc)*(1-tc)*fr.cx + 2*(1-tc)*tc*mx + tc*tc*tr.cx;
        const by = (1-tc)*(1-tc)*fr.cy + 2*(1-tc)*tc*my + tc*tc*tr.cy;
        
        if (t > 1.0) return null;
        return (
          <g key={`pulse-dot-${idx}`}>
            <circle cx={bx} cy={by} r="5" fill={stageColors[activeStage]} filter="url(#glow-light)" opacity="0.7"/>
            <circle cx={bx} cy={by} r="2" fill="#ffffff" />
          </g>
        );
      })}

      {BRAIN_REGIONS.map(region => {
        const isActive  = activeRegionSet.has(region.id);
        const isHovered = hovered === region.id;
        const scale = isActive ? (1 + Math.sin(tick * 0.15) * 0.04) : 1;

        return (
          <g key={region.id} className="transition-all duration-300">
            <circle
              cx={region.cx}
              cy={region.cy}
              r={region.rx * scale}
              fill={`url(#grad-${region.id})`}
              pointerEvents="none"
            />

            <circle
              cx={region.cx}
              cy={region.cy}
              r={region.rx * scale}
              fill="rgba(4, 10, 24, 0.45)"
              stroke={isActive ? region.activeColor : "rgba(148,163,184,0.18)"}
              strokeWidth={isActive ? "1.5" : "0.8"}
              strokeDasharray={isActive ? "none" : "3,4"}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHovered(region.id)}
              onMouseLeave={() => setHovered(null)}
            />

            <circle
              cx={region.cx}
              cy={region.cy}
              r={isActive ? 3.5 : 2.0}
              fill={isActive ? region.activeColor : "rgba(148,163,184,0.4)"}
              filter={isActive ? "url(#glow-light)" : "none"}
              pointerEvents="none"
            />

            {(isActive || isHovered) && (
              <g pointerEvents="none">
                <line 
                  x1={region.cx} 
                  y1={region.cy + (region.rx * scale)} 
                  x2={region.cx} 
                  y2={region.cy + (region.rx * scale) + 10} 
                  stroke={isActive ? region.activeColor : "#475569"} 
                  strokeWidth="0.8" 
                />
                <text
                  x={region.cx}
                  y={region.cy + (region.rx * scale) + 20}
                  textAnchor="middle"
                  fontSize="12"
                  fontFamily="monospace"
                  fontWeight="bold"
                  fill={isActive ? "#ffffff" : "#94a3b8"}
                  className="tracking-wider"
                >
                  {region.label.toUpperCase()}
                </text>
              </g>
            )}

            {isHovered && (
              <g pointerEvents="none" filter="url(#glow-heavy)" opacity="0.98">
                <rect
                  x={region.cx - 95}
                  y={region.cy - (region.rx * scale) - 36}
                  width="190"
                  height="28"
                  rx="4"
                  fill="#030712"
                  stroke={region.activeColor}
                  strokeWidth="1"
                />
                <text
                  x={region.cx}
                  y={region.cy - (region.rx * scale) - 18}
                  textAnchor="middle"
                  fontSize="9.5"
                  fontFamily="monospace"
                  fill="#ffffff"
                >
                  {region.desc}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

const NeuralPulsePage = () => {
  const terminalContainerRef = useRef(null);

  const [pulseData, setPulseData]     = useState(null);
  const [socHistory, setSocHistory]   = useState([]);
  const [aiLogs, setAiLogs]           = useState([]);
  const [activeTab, setActiveTab]     = useState("neuromodulators");
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  const isAtBottomRef = useRef(true);
  const [activeStage, setActiveStage] = useState(0);

  const calculateIntelligenceMetrics = () => {
    // Priority 1: Real-time Mathematical Cognitive Evolution from Neural Core
    if (pulseData?.cognitive_evolution) {
      const evo = pulseData.cognitive_evolution;
      return {
        baseline: String(evo.baseline_iq || "120.00"),
        current: String(evo.synthesized_iq || "124.50"),
        gain: String(evo.evolution_gain || "3.75"),
        retention: String(evo.retention_rate || "98.6"),
        nodes: Number(evo.experience_nodes || pulseData?.memory_nodes?.length || 0)
      };
    }

    // Dynamic Fallback based on real memory clusters count
    const entangledCount = pulseData?.memory_nodes?.length || 0;
    const baselineIQ = 120.00;
    const dynamicIQ = baselineIQ + Math.log(1 + entangledCount) * 3.5;
    const netGainPercent = ((dynamicIQ - baselineIQ) / baselineIQ) * 100;
    const retentionRate = 98.6;

    return {
      baseline: baselineIQ.toFixed(2),
      current: dynamicIQ.toFixed(2),
      gain: netGainPercent.toFixed(2),
      retention: retentionRate.toFixed(1),
      nodes: entangledCount
    };
  };

  const intelMetrics = pulseData ? calculateIntelligenceMetrics() : { baseline: "120.00", current: "120.00", gain: "0.00", retention: "98.6", nodes: 0 };
  const [activityLog, setActivityLog] = useState([]);
  const [eegHistory, setEegHistory]   = useState(() => Array(120).fill(0));

  // Parse COLLABORATIVE_DEBATE_LESSON from memory vault
  const parseDebateNode = useCallback((text) => {
    if (!text || !text.startsWith("COLLABORATIVE_DEBATE_LESSON:")) return null;
    try {
      const vulnMatch = text.match(/Vulnerability\[(.*?)\]/);
      const remMatch  = text.match(/TelemetryRemediation\[(.*?)\]/);
      return {
        vulnerability: vulnMatch ? vulnMatch[1] : "N/A",
        remediation:   remMatch ? remMatch[1] : "N/A"
      };
    } catch (e) {
      return null;
    }
  }, []);

  const [activeDebate, setActiveDebate] = useState(null);

  useEffect(() => {
    if (!pulseData?.memory_nodes) return;
    // Scan last 30 memory nodes for the latest debate lesson
    const debateNode = pulseData.memory_nodes.find(n => n.label && n.label.startsWith("COLLABORATIVE_DEBATE_LESSON:"));
    if (debateNode) {
      const parsed = parseDebateNode(debateNode.label);
      if (parsed) {
        setActiveDebate(parsed);
      }
    }
  }, [pulseData, parseDebateNode]);

  useEffect(() => {
    if (!pulseData) return;
    const state = pulseData.cognitive_state || "IDLE";
    const speed = (state === "THINKING" || state === "DREAMING" || state === "TACTICAL_REACTION") ? 900 : 4000;
    const interval = setInterval(() => setActiveStage(prev => (prev + 1) % 4), speed);
    return () => clearInterval(interval);
  }, [pulseData]);

  useEffect(() => {
    if (!aiLogs.length) return;
    const entries = aiLogs.slice(-20).map((log, i) => ({
      id: i,
      time: new Date().toLocaleTimeString("en-US", { hour12: false }),
      input:   log.substring(0, 40) + (log.length > 40 ? "…" : ""),
      process: ["EMBED→ATTN→MLP", "GQA 28-HEAD", "SwiGLU GATE", "LOGIT PROJ"][i % 4],
      output:  ["APPROVED", "BLOCK", "REVISE", "PASS"][i % 4],
      stage:   i % 4,
    }));
    setActivityLog(entries.reverse());
  }, [aiLogs]);

  useEffect(() => {
    if (!pulseData) return;
    const state = pulseData.cognitive_state || "IDLE";
    const amplitude = state === "TACTICAL_REACTION" ? 40 : state === "THINKING" ? 25 : state === "DREAMING" ? 18 : 8;
    const freq      = state === "TACTICAL_REACTION" ? 0.4  : state === "THINKING" ? 0.25 : state === "DREAMING" ? 0.15 : 0.08;
    const interval  = setInterval(() => {
      setEegHistory(prev => {
        const next = [...prev.slice(1)];
        const t    = Date.now() * freq * 0.01;
        const val  = Math.sin(t) * amplitude + Math.sin(t * 2.7) * (amplitude * 0.4) + (Math.random() - 0.5) * (amplitude * 0.3);
        next.push(val);
        return next;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [pulseData]);

  useEffect(() => {
    let active = true;
    const fetchTelemetry = async () => {
      try {
        const pulseRes = await axiosClient.get("/system/cognitive-pulse");
        const logsRes  = await axiosClient.get("/admin/system/ai-logs?limit=100");
        const socRes   = await axiosClient.get("/admin/soc/history?limit=5");
        if (active) {
          setPulseData(pulseRes);
          setAiLogs(logsRes.logs || []);
          setSocHistory(socRes || []);
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(err.message || "Failed to establish telemetry link.");
          setLoading(false);
        }
      }
    };
    fetchTelemetry();
    const interval = setInterval(fetchTelemetry, 1500);
    return () => { active = false; clearInterval(interval); };
  }, []);

  const handleTerminalScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 30;
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#020408] text-cyan-400 font-mono">
        <div className="text-center space-y-6">
          <div className="relative mx-auto w-20 h-20">
            <div className="absolute inset-0 rounded-full border border-cyan-950" />
            <div className="absolute inset-0 rounded-full border-t-2 border-cyan-400 animate-spin" />
          </div>
          <p className="text-xs tracking-[0.4em] text-cyan-500/80 uppercase animate-pulse">SYNCHRONIZING CORE CONSCIOUSNESS</p>
        </div>
      </div>
    );
  }

  const pulse        = pulseData || {};
  const metrics      = pulse.synaptic_metrics || {};
  const stateKey     = pulse.cognitive_state || "IDLE";
  const statePalette = STATE_PALETTE[stateKey] || STATE_PALETTE.IDLE;

  // Swapping status calculations
  const activeModel  = pulse.active_loras && pulse.active_loras.length > 0 ? "AIRS-Oculus-1.0 (Web Audit)" : "AIRSX-V1.2.0 (Security Core)";
  const isSwapping   = pulse.synaptic_metrics?.is_tactical_run && stateKey === "IDLE";

  const getActiveRegionSet = () => {
    const active = new Set();
    if (stateKey === "DREAMING") {
      PIPELINE_REGION_MAP.dreaming.forEach(r => active.add(r));
      PIPELINE_REGION_MAP.mlp.forEach(r => active.add(r));
    } else if (stateKey === "TACTICAL_REACTION") {
      PIPELINE_REGION_MAP.reaction.forEach(r => active.add(r));
    } else {
      const stageKey2 = ["embedding","attention","mlp","output"][activeStage];
      (PIPELINE_REGION_MAP[stageKey2] || []).forEach(r => active.add(r));
      PIPELINE_REGION_MAP.gate.forEach(r => active.add(r));
    }
    return active;
  };
  const activeRegionSet = getActiveRegionSet();

  const pipelineMetrics = [
    {
      title: "1. Embedding Layer", color: "border-cyan-950/40 text-cyan-400",
      activeColor: "border-cyan-500/50 bg-cyan-950/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]",
      badge: "INPUT_PROJECTION", math: "x_embedded = F.embedding(input_ids, W_embed)",
      details: [
        "Inp: Raw UTF-8 alphanumeric command character sequences.",
        "Ops: Tokenized via Qwen2.5 151,643-vocabulary dictionary.",
        "     Projected to a 3584-dimensional dense semantic vector space.",
        "Out: Token embeddings baseline tensor [B, T, 3584]."
      ]
    },
    {
      title: "2. Attention Heads", color: "border-emerald-950/40 text-emerald-400",
      activeColor: "border-emerald-500/50 bg-emerald-950/20 shadow-[0_0_15px_rgba(16,185,129,0.1)]",
      badge: "CONTEXT_ALIGNMENT", math: "Attention(Q, K, V) = softmax(Q K^T / sqrt(d_k)) V",
      details: [
        "Inp: Embedded token tensor aligned with spatial coordinates.",
        "Ops: Injecting RoPE (Rotary Positional Embeddings) at theta=1,000,000.",
        "     Grouped Query Attention (GQA) with 28 Query & 4 Key-Value heads.",
        "Out: Multi-head context-aligned relational representation."
      ]
    },
    {
      title: "3. MLP / Hidden Layers", color: "border-purple-950/40 text-purple-400",
      activeColor: "border-purple-500/50 bg-purple-950/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]",
      badge: "SYNAPTIC_RETR_ACT", math: "SwiGLU(x) = (Silu(x W_gate) * (x W_up)) W_down",
      details: [
        "Inp: Context-weighted relational attention tensor.",
        "Ops: Non-linear SwiGLU activation applied across feed-forward blocks.",
        "     Adrenaline scaled focus gating constraints applied.",
        "Out: High-entropy activated system state representation."
      ]
    },
    {
      title: "4. Output Logits", color: "border-amber-950/40 text-amber-400",
      activeColor: "border-amber-500/50 bg-amber-950/20 shadow-[0_0_15px_rgba(245,158,11,0.1)]",
      badge: "DECISION_LOGITS", math: "y_logits = x_norm W_lm_head",
      details: [
        "Inp: Normalized final layer hidden state representation.",
        "Ops: Projected to 151,643 vocabulary logit distribution map.",
        "     Temperature-adjusted Softmax verification applied.",
        "Out: High-probability next token ID (e.g. APPROVED or BLOCK)."
      ]
    }
  ];

  const stageColorClasses = ["text-cyan-400","text-emerald-400","text-purple-400","text-amber-400"];
  const stageOutputColors = ["text-emerald-400","text-red-400","text-amber-400","text-cyan-400"];

  return (
    <div className="min-h-screen bg-[#02040a] text-slate-200 p-6 space-y-6 font-sans selection:bg-cyan-500/30">

      {/* ── HEADER ── */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-5 border-b border-slate-900">
        <div className="space-y-1">
          <p className="text-[10px] font-mono tracking-[0.4em] text-cyan-500 uppercase font-bold">Appence · AIRS Security · Autonomous Sentinel</p>
          <h1 className="text-2xl font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-slate-100 via-slate-300 to-slate-500">COGNITIVE HUD ENGINE</h1>
          <p className="text-xs text-slate-500 font-mono">Live anatomical wireframe matrix & sensory firing telemetry</p>
        </div>
        
        {/* Dynamic Dual-Core Handoff Monitor  */}
        <div className="flex items-center gap-3">
          <div className="flex flex-col bg-slate-950 border border-slate-900 px-3 py-1.5 rounded-lg font-mono text-[10px]">
            <span className="text-slate-500">ACTIVE CORE CONTEXT:</span>
            <span className="text-cyan-400 font-bold tracking-wider">{activeModel.toUpperCase()}</span>
            {isSwapping && (
              <span className="text-amber-400 animate-pulse text-[8px] font-bold mt-0.5">EVACUATING VRAM / HOT-SWAPPING ACTIVE CONTEXT...</span>
            )}
          </div>
          <span className={`inline-flex items-center gap-2 text-[11px] font-mono font-bold px-4 py-2.5 rounded-lg border transition-all duration-300 backdrop-blur-md ${statePalette.pill}`}>
            <span className="h-2 w-2 rounded-full animate-ping" style={{ backgroundColor: statePalette.accent }} />
            {stateKey.replace("_"," ")}
          </span>
        </div>
      </div>

      {/* ── MAIN GRID MATRIX ── */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

        {/* ─── LEFT COLUMN ── */}
        <div className="xl:col-span-1 flex flex-col gap-5">
          <div className="bg-slate-950/40 border border-slate-900 rounded-xl overflow-hidden backdrop-blur-md shadow-2xl">
            <div className="flex border-b border-slate-900 bg-slate-950/60">
              <TabBtn id="neuromodulators" activeTab={activeTab} setActiveTab={setActiveTab} label="NEURO" />
              <TabBtn id="kv_cache"        activeTab={activeTab} setActiveTab={setActiveTab} label="KV"    />
              <TabBtn id="hardening"       activeTab={activeTab} setActiveTab={setActiveTab} label="SEC"   />
              <TabBtn id="mitigations"     activeTab={activeTab} setActiveTab={setActiveTab} label="SOC"   />
            </div>
            <div className="p-4 min-h-[140px]">
              {activeTab === "neuromodulators" && (
                <div className="space-y-4">
                  <MetricBar label="Adrenaline (Focus Scope)"    value={pulse.neuromodulators?.adrenaline} max={2.5} color="bg-cyan-500"    textColor="text-cyan-400" />
                  <MetricBar label="Dopamine (Synaptic Weight)" value={pulse.neuromodulators?.dopamine}   max={3.0} color="bg-purple-500" textColor="text-purple-400" />
                  <p className="text-[10px] font-mono text-slate-500 bg-slate-950/80 rounded-lg p-2.5 border border-slate-900/50 leading-relaxed">
                    Adrenaline regulates decoding precision dynamically. Dopamine handles learning weights correction pace.
                  </p>
                </div>
              )}
              {activeTab === "kv_cache" && (
                <div className="space-y-0.5 text-[11px] font-mono">
                  {[
                    ["KV Cache Engine",  <span key="v" className="text-emerald-400 font-bold">ACTIVE_PAGED</span>],
                    ["Layer Maps Stack", <span key="v" className="text-slate-300">28 / 28 Blocks</span>],
                    ["Block Allocation", <span key="v" className="text-slate-400">256 Tokens</span>],
                    ["Context Ceiling",  <span key="v" className="text-slate-400">4096 Tokens</span>],
                  ].map(([l, v], i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-slate-900/40 last:border-0">
                      <span className="text-slate-500">{l}</span>{v}
                    </div>
                  ))}
                </div>
              )}
              {activeTab === "hardening" && (
                <div className="space-y-0.5 text-[11px] font-mono">
                  {[
                    ["Kernel Protection", <span key="v" className="text-cyan-400 font-bold">MUTEX_LOCKED</span>],
                    ["Anti-Tracer PID",   <span key="v" className="text-emerald-400">SAFE (0)</span>],
                    ["Dump Intercept",    <span key="v" className="text-slate-400">RESTRICTED</span>],
                    ["Assurance Verdict", <span key="v" className={`font-bold ${metrics?.assurance_verdict === "REVISE" ? "text-red-400 animate-pulse" : "text-emerald-400"}`}>{metrics?.assurance_verdict || "APPROVED"}</span>],
                  ].map(([l, v], i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-slate-900/40 last:border-0">
                      <span className="text-slate-500">{l}</span>{v}
                    </div>
                  ))}
                  <div className="border-t border-slate-900/60 pt-2.5 mt-2">
                    <span className="text-slate-500 block mb-1.5 uppercase font-bold text-[10px] tracking-wider">Active Adaptations (LoRA):</span>
                    {pulse.active_loras && pulse.active_loras.length > 0 ? (
                      <div className="space-y-1">
                        {pulse.active_loras.map(([name, info], idx) => (
                          <div key={idx} className="flex justify-between items-center bg-slate-950 p-2 border border-slate-900 rounded-lg">
                            <span className="text-purple-400 truncate font-semibold text-[10px]">{name}</span>
                            <span className="text-slate-600 text-[8px] font-bold">ACTIVE</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-600 italic text-[10px]">No active weights adapter mapped (Base weights default).</span>
                    )}
                  </div>
                </div>
              )}
              {activeTab === "mitigations" && (
                <div className="space-y-2 max-h-[140px] overflow-y-auto custom-scrollbar">
                  {socHistory.length > 0 ? socHistory.map((item, i) => (
                    <div key={i} className="rounded-lg border border-slate-900 bg-slate-950/80 p-2 space-y-1 font-mono">
                      <div className="flex justify-between text-[9px]">
                        <span className="text-red-400 font-bold">{item.action_taken}</span>
                        <span className="text-slate-600">{new Date(item.executed_at).toLocaleTimeString()}</span>
                      </div>
                      <p className="text-[9px] text-slate-500 truncate">{item.reasoning}</p>
                    </div>
                  )) : <p className="text-[11px] font-mono text-slate-600 italic">No mitigations synchronized.</p>}
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-4 backdrop-blur-md">
            <p className="text-[10px] font-mono font-bold tracking-widest text-cyan-400 mb-3">CLUSTER SCHEMATICS</p>
            <div className="space-y-2">
              {[
                { color:"#06b6d4", label:"Prefrontal Cortex", role:"Embedding" },
                { color:"#10b981", label:"Motor Pathway",    role:"Attention" },
                { color:"#a855f7", label:"Parietal / Temp",   role:"MLP Hidden" },
                { color:"#f59e0b", label:"Occipital Stack",   role:"Logits Out" },
                { color:"#8b5cf6", label:"Cerebellar Loop",   role:"Dreaming" },
                { color:"#ef4444", label:"Brainstem Bus",     role:"Reaction Gate" },
              ].map(({ color, label, role }) => (
                <div key={label} className="flex items-center gap-2.5 text-[10px] font-mono">
                  <span className="w-2 h-2 rounded-full shrink-0 shadow-[0_0_6px_currentColor]" style={{ backgroundColor: color, color: color }} />
                  <span className="text-slate-400 flex-1 truncate">{label}</span>
                  <span className="text-slate-600 text-[9px]">{role}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-4 backdrop-blur-md flex-1 flex flex-col justify-between">
            <div>
              <p className="text-[10px] font-mono font-bold tracking-widest text-purple-400 mb-3">RESONATED COGNITIONS</p>
              <div className="space-y-1.5 max-h-60 overflow-y-auto custom-scrollbar">
                {pulse.memory_nodes?.slice(0, 5).map((node, i) => (
                  <div key={i} className="flex justify-between items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-950/90 border border-slate-900 font-mono text-[10px]">
                    <span className="text-slate-400 truncate">{node.label}</span>
                    <span className="text-cyan-500/70 text-[9px] font-bold">W:{node.activity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ─── CENTER COLUMN ── */}
        <div className="xl:col-span-2 flex flex-col gap-5">
          <div className="bg-[#010408]/60 border border-slate-900 rounded-xl overflow-hidden relative flex-1 flex flex-col p-4 shadow-2xl">
            <div className="absolute top-5 left-5 z-10 pointer-events-none font-mono">
              <p className="text-xs font-bold tracking-widest text-cyan-400">ANATOMICAL BRAIN WIREFRAME</p>
              <p className="text-[9px] text-slate-600 mt-0.5">Calibrated target cluster alignment vectors</p>
            </div>
            
            <div className="absolute top-5 right-5 z-10 flex gap-1.5">
              {["Embed", "Attn", "MLP", "Output"].map((s, i) => (
                <div key={i} className={`text-[9px] font-mono font-bold px-2.5 py-1 rounded transition-all duration-300 border ${
                  activeStage === i
                    ? ["border-cyan-500 text-cyan-400 bg-cyan-950/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]",
                       "border-emerald-500 text-emerald-400 bg-emerald-950/20 shadow-[0_0_10px_rgba(16,185,129,0.1)]",
                       "border-purple-500 text-purple-400 bg-purple-950/20 shadow-[0_0_10px_rgba(168,85,247,0.1)]",
                       "border-amber-500 text-amber-400 bg-amber-950/20 shadow-[0_0_10px_rgba(245,158,11,0.1)]"][i]
                    : "border-slate-900 text-slate-700 bg-transparent"
                }`}>
                  {s.toUpperCase()}
                </div>
              ))}
            </div>

            <div className="w-full flex items-center justify-center flex-1 py-6" style={{ aspectRatio: "600/480" }}>
              <BrainSVG
                activeRegionSet={activeRegionSet}
                pulseData={pulseData}
                activeStage={activeStage}
                state={stateKey}
              />
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-sky-400 border-b border-slate-800 pb-2 mb-3">Sovereign Cognitive Evolution</h3>
              <div className="grid grid-cols-2 gap-2 bg-[#040814] p-3 border border-slate-900 rounded-lg font-mono text-[10px]">
                <div className="border-r border-slate-900/60 pr-2">
                  <span className="text-slate-500 block mb-0.5">BASELINE IQ</span>
                  <span className="text-slate-400 font-bold">{intelMetrics.baseline}</span>
                </div>
                <div className="pl-2">
                  <span className="text-slate-500 block mb-0.5">SYNTHESIZED IQ</span>
                  <span className="text-cyan-400 font-bold filter drop-shadow-[0_0_3px_#22d3ee]">{intelMetrics.current}</span>
                </div>
                <div className="col-span-2 border-t border-slate-900/60 pt-2 flex justify-between">
                  <div>
                    <span className="text-slate-500 block mb-0.5">EVOLUTION GAIN</span>
                    <span className="text-emerald-400 font-bold">+{intelMetrics.gain}%</span>
                  </div>
                  <div className="text-right">
                    <span className="text-slate-500 block mb-0.5">EXPERIENCE NODES</span>
                    <span className="text-purple-400 font-bold">{intelMetrics.nodes} entangled</span>
                  </div>
                </div>
                <div className="col-span-2 border-t border-slate-900/60 pt-2 flex justify-between">
                  <span className="text-slate-500">EXPERIENCE RETENTION:</span>
                  <span className="text-slate-300 font-bold">{intelMetrics.retention}%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-950/50 border border-slate-900 rounded-xl p-4 backdrop-blur-md">
            <div className="flex justify-between items-center mb-2 font-mono">
              <p className="text-[10px] font-bold tracking-widest text-emerald-400">SYNAPTIC OSCILLOSCOPE (EEG)</p>
              <span className="text-[9px] text-slate-600 uppercase">Frequency Scale Modulator</span>
            </div>
            <div className="bg-slate-950/90 rounded-lg p-2 border border-slate-900">
              <EegCanvas eegHistory={eegHistory} state={stateKey} />
            </div>
          </div>
        </div>

        {/* ─── RIGHT COLUMN ── */}
        <div className="xl:col-span-2 flex flex-col gap-5">
          <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-5 backdrop-blur-md flex-1">
            <div className="pb-3 mb-4 border-b border-slate-900 font-mono">
              <p className="text-xs font-bold tracking-widest text-cyan-400">COGNITIVE TRANSFORMER PIPELINE</p>
              <p className="text-[10px] text-slate-500 mt-1">Mathematical tensor trace of active neural computing phases</p>
            </div>
            <div className="space-y-3">
              {pipelineMetrics.map((stage, idx) => {
                const is_active = activeStage === idx;
                return (
                  <div key={idx} className={`border rounded-xl p-4 transition-all duration-500 ${is_active ? `${stage.activeColor} scale-[1.01]` : `${stage.color} bg-transparent opacity-20`}`}>
                    <div className="flex justify-between items-center gap-2 mb-2 font-mono">
                      <span className="text-xs font-bold text-slate-200">{stage.title}</span>
                      <span className="text-[8px] tracking-wider px-2 py-0.5 rounded border border-slate-800 text-slate-400 font-bold bg-slate-900/40">{stage.badge}</span>
                    </div>
                    <div className="font-mono text-[10px] text-cyan-400 bg-slate-950/90 px-3 py-2 rounded-lg border border-slate-900 overflow-x-auto select-all shadow-inner">
                      {stage.math}
                    </div>
                    {is_active && (
                      <div className="mt-2.5 space-y-0.5 border-t border-slate-900/50 pt-2">
                        {stage.details.map((line, i) => (
                          <p key={i} className="text-[10px] font-mono text-slate-400 whitespace-pre leading-relaxed">{line}</p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-5 backdrop-blur-md flex-1 flex flex-col">
            <div className="pb-3 mb-3 border-b border-slate-900 flex justify-between items-center font-mono">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                <p className="text-xs font-bold tracking-widest text-amber-500">I/O ACTIVITY STREAM</p>
              </div>
              <span className="text-[9px] text-slate-600">INPUT ── PROCESS ── LOGIT</span>
            </div>
            <div className="grid grid-cols-12 gap-1 text-[9px] font-mono text-slate-500 uppercase tracking-wider px-2 pb-2 mb-1 border-b border-slate-900/30">
              <span className="col-span-1">SIG</span>
              <span className="col-span-3">TIMESTAMP</span>
              <span className="col-span-3">RAW INPUT</span>
              <span className="col-span-3">ROUTING</span>
              <span className="col-span-2 text-right">OUTPUT</span>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar flex-1">
              {activityLog.length > 0 ? activityLog.map(entry => (
                <div key={entry.id} className="grid grid-cols-12 gap-1 text-[10px] font-mono px-2 py-2 rounded-lg bg-slate-950/30 hover:bg-slate-900/40 border border-transparent hover:border-slate-900 transition-all">
                  <span className={`col-span-1 font-bold ${stageColorClasses[entry.stage]}`}>#{entry.id}</span>
                  <span className="col-span-3 text-slate-500">{entry.time}</span>
                  <span className="col-span-3 text-slate-300 truncate" title={entry.input}>{entry.input}</span>
                  <span className="col-span-3 text-purple-400/80 text-[9px]">{entry.process}</span>
                  <span className={`col-span-2 font-bold text-right ${stageOutputColors[entry.stage]}`}>{entry.output}</span>
                </div>
              )) : (
                <p className="text-[11px] font-mono text-slate-600 italic px-2 py-3">Awaiting input impulses…</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Federated Model‑to‑Model Debate Terminal (Dynamic Integration Layer)  */}
      {activeDebate && (
        <div className="bg-slate-950/40 border border-purple-500/30 rounded-xl p-5 backdrop-blur-md shadow-[0_0_20px_rgba(168,85,247,0.05)]">
          <div className="flex justify-between items-center pb-3 mb-3 border-b border-slate-900 font-mono">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-purple-500 animate-ping" />
              <p className="text-xs font-bold tracking-widest text-purple-400">FEDERATED INTELLIGENCE FUSION (MODEL-TO-MODEL DEBATE)</p>
            </div>
            <span className="text-[10px] text-slate-600">Dynamic Exploit Simulation & Defensive Remediation Loop</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-mono text-xs">
            {/* Red Team Oculus Output  */}
            <div className="bg-red-950/10 border border-red-950/40 p-4 rounded-lg space-y-2">
              <div className="flex justify-between items-center text-[10px] border-b border-red-950/30 pb-1.5">
                <span className="text-red-400 font-bold">CORE-BETA (AIRS-OCULUS-1.0)</span>
                <span className="text-red-600 font-bold">RED_TEAM_PROJECTION</span>
              </div>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                {activeDebate.vulnerability}
              </p>
            </div>
            {/* Blue Team AIRSX Output  */}
            <div className="bg-cyan-950/10 border border-cyan-950/40 p-4 rounded-lg space-y-2">
              <div className="flex justify-between items-center text-[10px] border-b border-cyan-950/30 pb-1.5">
                <span className="text-cyan-400 font-bold">CORE-ALPHA (AIRSX-V1.2.0)</span>
                <span className="text-cyan-600 font-bold">BLUE_TEAM_REMEDIATION</span>
              </div>
              <p className="text-slate-300 leading-relaxed text-[11px] whitespace-pre-line">
                {activeDebate.remediation}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── BOTTOM TERMINAL STREAM ── */}
      <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-5 backdrop-blur-md">
        <div className="flex justify-between items-center pb-3 mb-3 border-b border-slate-900 font-mono">
          <div className="flex items-center gap-2">
            <TerminalIcon />
            <p className="text-xs font-bold tracking-widest text-amber-500">SOVEREIGN INNER MONOLOGUE STREAM</p>
          </div>
          <span className="text-[10px] text-slate-600">Uncensored Linear Cognition Pipeline Trace</span>
        </div>
        <div
          ref={terminalContainerRef}
          onScroll={handleTerminalScroll}
          className="h-44 overflow-y-auto font-mono text-[11px] text-slate-400 space-y-1 bg-[#010204] p-4 rounded-lg border border-slate-900 shadow-inner custom-scrollbar"
        >
          {aiLogs.length > 0 ? aiLogs.map((log, i) => (
            <div key={i} className="leading-relaxed border-b border-slate-950 pb-1.5 last:border-0 flex gap-3 hover:text-slate-200 transition-colors">
              <span className="text-amber-500/50 font-bold shrink-0 select-none">{`[AXON_${i}]`}</span>
              <span className="break-all">{log}</span>
            </div>
          )) : <p className="text-slate-700 italic">Listening for continuous synaptic reflections…</p>}
        </div>
      </div>
    </div>
  );
};

export default NeuralPulsePage;